import { Stethoscope, Building2, Check, ChevronDown } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useUserContexts, UserContext } from "@/hooks/useUserContexts";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { useNavigate, useLocation } from "react-router-dom";

const CONTEXT_CONFIG = {
  professional: {
    label: "Professional Tools",
    icon: Stethoscope,
    shortLabel: "Professional",
    color: "text-primary",
    defaultRoute: "/professional"
  },
  site: {
    label: "Site Management",
    icon: Building2,
    shortLabel: "Site",
    color: "text-secondary",
    defaultRoute: "/site-dashboard"
  }
} as const;

export function ContextSwitcher() {
  const { contexts, activeContext, setActiveContext, loading } = useUserContexts();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingContext, setPendingContext] = useState<UserContext | null>(null);

  if (loading) {
    return <Skeleton className="h-10 w-[180px]" />;
  }

  // Don't show if user only has one context
  if (contexts.length <= 1) {
    return null;
  }

  const handleContextSwitch = (newContext: UserContext) => {
    if (newContext === activeContext) return;

    // Show confirmation dialog
    setPendingContext(newContext);
    setShowConfirmDialog(true);
  };

  const confirmContextSwitch = () => {
    if (!pendingContext) return;

    const config = CONTEXT_CONFIG[pendingContext];
    setActiveContext(pendingContext);

    // Show toast notification
    toast({
      title: "Context Switched",
      description: `Switched to ${config.label}`,
    });

    // Smart navigation: if current page isn't available in new context, redirect to default
    const currentPath = location.pathname;
    
    // Define context-specific pages
    const professionalOnlyPages = ['/professional', '/professional-analytics', '/referrals/intake', '/referrals/review'];
    const siteOnlyPages = ['/site-dashboard', '/dashboard', '/site-assessment', '/trial-site'];
    
    const shouldRedirect = (
      (pendingContext === 'professional' && siteOnlyPages.some(p => currentPath.startsWith(p))) ||
      (pendingContext === 'site' && professionalOnlyPages.some(p => currentPath.startsWith(p)))
    );

    if (shouldRedirect) {
      navigate(config.defaultRoute);
    }

    setShowConfirmDialog(false);
    setPendingContext(null);
  };

  const currentConfig = activeContext ? CONTEXT_CONFIG[activeContext] : null;
  const CurrentIcon = currentConfig?.icon || Stethoscope;

  const pendingConfig = pendingContext ? CONTEXT_CONFIG[pendingContext] : null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            className="h-10 gap-2 px-3 bg-background/95 hover:bg-accent/50 transition-all duration-200 hover:scale-105"
          >
            <CurrentIcon className={cn("h-4 w-4", currentConfig?.color)} />
            <span className="max-w-[120px] truncate font-medium">
              {currentConfig?.shortLabel || "Select View"}
            </span>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[240px] bg-background/95 backdrop-blur-sm z-[100] animate-scale-in">
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            Switch View
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {contexts.map((context) => {
            const config = CONTEXT_CONFIG[context];
            const Icon = config.icon;
            const isActive = context === activeContext;
            
            return (
              <DropdownMenuItem
                key={context}
                onClick={() => handleContextSwitch(context)}
                className={cn(
                  "flex items-center gap-3 p-3 cursor-pointer transition-colors",
                  isActive && "bg-accent"
                )}
              >
                <Icon className={cn("h-4 w-4", config.color)} />
                <span className="flex-1 font-medium text-sm">
                  {config.label}
                </span>
                {isActive && (
                  <Check className="h-4 w-4 text-primary" />
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch Context?</AlertDialogTitle>
            <AlertDialogDescription>
              You're about to switch to <strong>{pendingConfig?.label}</strong>. 
              This will change your navigation menu and available features.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingContext(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmContextSwitch}>
              Switch Context
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
